import _ from 'lodash';
import moment from 'moment';
// The Strict* form deliberately: the loose `CheckboxProps` adds
// `[key: string]: any`, which would re-admit `any` on every property read.
import type { StrictCheckboxProps } from 'semantic-ui-react';
import {
  Checkbox,
  Icon,
  Loader,
  Popup,
  Table,
  Message,
  MessageList,
  MessageHeader,
} from 'semantic-ui-react';
import type { FoodRecommendation, FoodRecommendationNote } from 'types';
import { mapKey, sortDirection } from 'utils';

import { statusColors } from './constants';

/**
 * What the food-recommendations table accepts. Single call site in
 * `./index.tsx`; cross-checked against `./table.spec.tsx`'s `BASE_PROPS`.
 *
 * `org` and `vendor` are passed and read nowhere here — neither appears in any
 * of this table's columns.
 */
type FoodRecommendationsTableProps = {
  /** Fires as each row's checkbox toggles. */
  addExportData: (
    event: React.FormEvent<HTMLInputElement>,
    checkprops: StrictCheckboxProps,
    id?: string,
  ) => void;
  /**
   * The currently sorted column key. The spec seeds it `''`; the parent seeds `null`
   * and passes `state.sortcolumn`, which is `undefined` until the reducer's first
   * `CHANGE_SORT` — hence the explicit `| undefined`.
   */
  column?: string | null | undefined;
  /** Rows to render. `| undefined` explicit: the spec passes `data={undefined}`. */
  data?: FoodRecommendation[] | null | undefined;
  /** Sort direction, forwarded to Semantic's `sorted`. */
  direction?: 'ascending' | 'descending' | null;
  /** The parent's `useReducer` dispatch; every call here is a `CHANGE_SORT`. */
  dispatch: (action: { type: string; sortcolumn?: string }) => void;
  /** Opens the roster-wide member drawer from a row. */
  openSelectedPatient: (patientId?: string) => void;
  /** Accepted but unread — see the type doc above. Passed as `props.org`, itself optional. */
  org?: string | undefined;
  /**
   * Members keyed by id; supplies the member-name column.
   *
   * Open-keyed: the subscript is a row's own `patientId`, so a row naming a member
   * this map was not built for reads `undefined` — which is why the read site tests
   * `patients[id]` first and renders a loader otherwise.
   *
   * Declared by what this table READS — `firstName` and `lastName`, at the single
   * read site — rather than as a whole `FlatPatient`. The parent passes a full
   * `FlatPatient` map, which satisfies this; requiring the full 23-field record here
   * would be a demand no column in this table makes.
   *
   * The two name fields are `unknown` rather than `FlatPatient`'s `unknown[]`, because
   * both shapes genuinely arrive: the parent passes `parseFHIRPatient` output, whose
   * names are `unknown[]` (fhirpath always returns an array), while `./table.spec.tsx`
   * passes plain strings. The read site is `rowPatient.firstName + ' ' + lastName`,
   * which is total over both — a one-element array stringifies to its element, so the
   * spec's strings and the parent's arrays render the same text.
   */
  patients?: Record<string, { firstName?: unknown; lastName?: unknown } | undefined> | null;
  /**
   * Absent reveals the member-name column, as `./table.spec.tsx` pins.
   *
   * `| undefined` is explicit: under `exactOptionalPropertyTypes` a bare `?` admits
   * the property being missing but rejects it being PRESENT and holding `undefined`,
   * and the spec's `BASE_PROPS` spreads exactly that to exercise this path.
   */
  patientId?: string | null | undefined;
  /** Header checkbox handler. */
  selectAll: (event: React.FormEvent<HTMLInputElement>, checkprops: StrictCheckboxProps) => void;
  /** Ids currently ticked. */
  selectedOrderIds: string[];
  /** Accepted but unread — see the type doc above. Passed as `props.vendor`, itself optional. */
  vendor?: string | undefined;
};

function FoodRecommendationsTable(props: FoodRecommendationsTableProps) {
  return (
    <>
      <div className="table-div">
        <Table sortable celled singleLine>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width={1} content={<Checkbox onChange={props.selectAll} />} />
              <Table.HeaderCell width={1}>Status</Table.HeaderCell>
              <Table.HeaderCell width={1}>Food Recomm Id</Table.HeaderCell>
              <Table.HeaderCell width={1}>Order Id</Table.HeaderCell>
              {!props.patientId && (
                <Table.HeaderCell
                  width={1}
                  {...sortDirection(props.column === 'firstName' ? props.direction : null)}
                  onClick={() =>
                    props.dispatch({
                      type: 'CHANGE_SORT',
                      sortcolumn: `firstName`,
                    })
                  }
                >
                  Member Name
                </Table.HeaderCell>
              )}
              <Table.HeaderCell
                {...sortDirection(props.column === 'createdDateTime' ? props.direction : null)}
                onClick={() =>
                  props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'createdDateTime' })
                }
              >
                Date Ran
              </Table.HeaderCell>

              <Table.HeaderCell
                {...sortDirection(props.column === 'patientOrg' ? props.direction : null)}
                onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'patientOrg' })}
              >
                Program
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(props.column === 'patientOrg' ? props.direction : null)}
                onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'patientOrg' })}
              >
                Vendor Rule
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(props.column === 'patientOrg' ? props.direction : null)}
                onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'patientOrg' })}
              >
                Allergy Rule
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(props.column === 'patientOrg' ? props.direction : null)}
                onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'patientOrg' })}
              >
                Medical Condition Rule
              </Table.HeaderCell>
              <Table.HeaderCell
                {...sortDirection(props.column === 'patientOrg' ? props.direction : null)}
                onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'patientOrg' })}
              >
                Food Pref Rule
              </Table.HeaderCell>
              <Table.HeaderCell>Meal Count Rule</Table.HeaderCell>

              {/* <Table.HeaderCell
              {...sortDirection(props.column === 'orderDate' ? props.direction : null)}
              onClick={() => props.dispatch({ type: 'CHANGE_SORT', sortcolumn: 'orderDate' })}
            >
              Recommended Food
            </Table.HeaderCell> */}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {props?.data?.map((order) => {
              const orderStatus = order.status && order.status.toLowerCase();
              const colorCode = statusColors[mapKey(orderStatus)];

              /*
               * Annotated because `{}` has no index signature; `noteCode` is
               * optional on the note, and the `hasOwnProperty` test establishes
               * presence without narrowing it.
               *
               * `warningMessage` was intersected on locally while this file looked
               * like the only evidence the recommender sends it. `draftOrders/table.tsx`
               * reads the same field off the same notes in four more columns, so it now
               * lives on {@link FoodRecommendationNote}.
               */
              const recommenderNotes: Record<string, FoodRecommendationNote | undefined> = {};
              order?.notes?.forEach((note) => {
                if (Object.prototype.hasOwnProperty.call(note, 'noteCode')) {
                  recommenderNotes[mapKey(note.noteCode)] = { ...note };
                }
              });

              const foodRecommTime = order.createdDateTime
                ? moment.utc(order.createdDateTime).local().format('YYYY-MM-DD hh:mm a')
                : '';

              // let orderTask = [`Task Id: ${order?.task?.id}`, `Due Date:
              // ${order?.task?.dueDate}`];
              const orderTask: string[] = [];
              for (const key in order.task) {
                // Deliberate string coercion of a deliberately wide declared type; the runtime
                // values are scalars.
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                orderTask.push(`${key} : ${order.task[key]}`);
              }

              const summaryContent = (
                <>
                  <Message info>
                    <MessageHeader>
                      <Icon name="food"></Icon> Task Summary
                    </MessageHeader>
                    <MessageList items={orderTask} />
                  </Message>
                  <Message info>
                    <MessageHeader>
                      <Icon name="food"></Icon> Food Recommendation Summary @ {foodRecommTime}
                    </MessageHeader>
                    {/*
                      Spread rather than `items={order?.summaryText}`: Semantic declares
                      `items?: SemanticShorthandCollection<...>`, which under
                      `exactOptionalPropertyTypes` admits the prop being ABSENT but not
                      present-and-undefined. `MessageList` renders it through lodash
                      `map`, which yields `[]` for `undefined` and for an omitted prop
                      alike (verified against the installed lodash), so omitting it is
                      what the runtime already did with the optional chain.
                    */}
                    <MessageList {...(order?.summaryText ? { items: order.summaryText } : {})} />
                  </Message>
                </>
              );

              return (
                <Table.Row
                  positive={colorCode === 'yy'}
                  warning={colorCode === 'warning'}
                  negative={colorCode === 'negative'}
                  error={colorCode === 'error'}
                  style={{
                    backgroundColor:
                      (colorCode === 'blue' && '#F0F8FF') || (colorCode === 'yellow' && '#ffefd5'),
                    color: colorCode === 'zz' && '#808080',
                  }}
                >
                  <Table.Cell
                    width={1}
                    collapsing
                    content={
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Checkbox
                          checked={props.selectedOrderIds.includes(order.id!)}
                          onChange={(e, checkprops) => props.addExportData(e, checkprops, order.id)}
                        />
                      </div>
                    }
                  />
                  <Table.Cell>
                    <>
                      {order?.orderId ? (
                        <Icon name="thumbs up" color="green" size="large" />
                      ) : (
                        <Icon name="thumbs down" color="red" size="large" />
                      )}
                      <Popup
                        wide={order?.foodRecommendationNotes ? 'very' : true}
                        on="click"
                        // position="top right"
                        closeOnEscape
                        closeOnDocumentClick
                        content={summaryContent}
                        flowing
                        trigger={
                          <Icon
                            size="large"
                            style={{ fontSize: 18 }}
                            color="blue"
                            link
                            name="clipboard list"
                          />
                        }
                      />
                    </>
                  </Table.Cell>
                  <Table.Cell>{order.id}</Table.Cell>
                  <Table.Cell>{order.orderId}</Table.Cell>
                  {!props.patientId && (
                    <Table.Cell>
                      <a
                        onClick={
                          !props.patientId
                            ? (e) => {
                                e.preventDefault();
                                props.openSelectedPatient(order.patientId);
                              }
                            : (null as unknown as React.MouseEventHandler<HTMLAnchorElement>)
                        }
                        style={{ cursor: 'pointer' }}
                      >
                        {props.patients && props.patients[order.patientId!] ? (
                          // Concatenation, not arithmetic: the operands coerce to string as
                          // upstream intends. Wrapping them in String() would emit a call.
                          /* eslint-disable @typescript-eslint/restrict-plus-operands */
                          props.patients[order.patientId!]!.firstName +
                          ' ' +
                          props.patients[order.patientId!]!.lastName
                        ) : (
                          /* eslint-enable @typescript-eslint/restrict-plus-operands */
                          <Loader size="tiny" active inline />
                        )}
                      </a>
                    </Table.Cell>
                  )}
                  <Table.Cell>{foodRecommTime}</Table.Cell>

                  <Table.Cell>{order?.clientOrgId}</Table.Cell>
                  <Table.Cell>
                    {recommenderNotes['vendor-rule']?.message}
                    {
                      <>
                        &nbsp;&nbsp;&nbsp;
                        {recommenderNotes['vendor-rule']?.warningMessage !== undefined ? (
                          <Popup
                            on="click"
                            position="bottom right"
                            wide
                            trigger={<Icon name="sticky note" color="red" link />}
                            content={recommenderNotes['vendor-rule']?.warningMessage}
                          />
                        ) : (
                          ''
                        )}
                      </>
                    }
                  </Table.Cell>
                  <Table.Cell>
                    {recommenderNotes['allergy-rule']?.message}
                    {
                      <>
                        &nbsp;&nbsp;&nbsp;
                        {recommenderNotes['allergy-rule']?.warningMessage !== undefined ? (
                          <Popup
                            on="click"
                            position="bottom right"
                            wide
                            trigger={<Icon name="sticky note" color="red" link />}
                            content={recommenderNotes['allergy-rule']?.warningMessage}
                          />
                        ) : (
                          ''
                        )}
                      </>
                    }
                  </Table.Cell>
                  <Table.Cell>
                    {recommenderNotes['medical-condition-rule']?.message}
                    {
                      <>
                        &nbsp;&nbsp;&nbsp;
                        {recommenderNotes['medical-condition-rule']?.warningMessage !==
                        undefined ? (
                          <Popup
                            on="click"
                            position="bottom right"
                            wide
                            trigger={<Icon name="sticky note" color="red" link />}
                            content={recommenderNotes['medical-condition-rule']?.warningMessage}
                          />
                        ) : (
                          ''
                        )}
                      </>
                    }
                  </Table.Cell>
                  <Table.Cell>
                    {recommenderNotes['food-preference-rule']?.message}
                    {
                      <>
                        &nbsp;&nbsp;&nbsp;
                        {recommenderNotes['food-preference-rule']?.warningMessage !== undefined ? (
                          <Popup
                            on="click"
                            position="bottom right"
                            wide
                            trigger={<Icon name="sticky note" color="red" link />}
                            content={recommenderNotes['food-preference-rule']?.warningMessage}
                          />
                        ) : (
                          ''
                        )}
                      </>
                    }
                  </Table.Cell>
                  <Table.Cell>
                    {recommenderNotes['meal-count-rule']?.message}
                    {
                      <>
                        &nbsp;&nbsp;&nbsp;
                        {recommenderNotes['meal-count-rule']?.warningMessage !== undefined ? (
                          <Popup
                            on="click"
                            position="bottom right"
                            wide
                            trigger={<Icon name="sticky note" color="red" link />}
                            content={recommenderNotes['meal-count-rule']?.warningMessage}
                          />
                        ) : (
                          ''
                        )}
                      </>
                    }
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </div>
      <p style={{ margin: '25px 0 10px 15px', color: '#0067a5', fontSize: '17px' }}>
        Results Filtered: {props.data && props.data.length}
      </p>
    </>
  );
}

export default FoodRecommendationsTable;
